import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type CategoryMeta, type VisitorCounts } from '../api.ts';
import { ArticleCard } from '../components/ArticleCard.tsx';
import type { ArticleSummary, Category } from '../../../shared/types.ts';

const CATEGORY_ORDER: Category[] = ['tutorial', 'project', 'curiosity', 'qa', 'cheatsheet', 'library'];

export function Home(): JSX.Element {
  const [articles, setArticles] = useState<ArticleSummary[] | null>(null);
  const [cats, setCats] = useState<Record<Category, CategoryMeta> | null>(null);
  const [stats, setStats] = useState<{ published: number; planned: number; total: number; visitors: VisitorCounts } | null>(null);

  useEffect(() => {
    api.listArticles().then(d => { setArticles(d.articles); setCats(d.categories); });
    api.stats().then(s => setStats({ published: s.published, planned: s.planned, total: s.total, visitors: s.visitors }));
  }, []);

  if (!articles || !cats) return <div className="center-pad"><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  const featured = articles.slice(0, 6);

  return (
    <div className="page">
      <section className="hero">
        <h1>Learn Python by reading, building, and tinkering.</h1>
        <p>
          Tutorials that go deep, guided projects that teach by doing, curiosities that show you how
          Python actually works, and answers to the questions you'd otherwise Google.
        </p>
        {stats && (
          <div className="stats">
            <span><strong>{stats.published}</strong> articles live</span>
            <span><strong>{stats.planned}</strong> planned</span>
            <span><strong>6</strong> categories</span>
            <span><strong>{stats.visitors.weekly.toLocaleString()}</strong> visitors this week</span>
            <span><strong>{stats.visitors.total.toLocaleString()}</strong> all-time</span>
          </div>
        )}
      </section>

      <div className="cat-strip">
        {CATEGORY_ORDER.map(c => (
          <Link key={c} to={`/category/${c}`} className={`cat-${c}`}>
            <div className="cat-bar" style={{ background: `var(--cat-${c})` }} />
            <div className="cat-title">{cats[c].label}</div>
            <div className="cat-blurb">{cats[c].blurb}</div>
          </Link>
        ))}
      </div>

      <div className="section-head">
        <h2>Latest articles</h2>
        <Link to="/roadmap">See what's coming →</Link>
      </div>
      <div className="article-grid">
        {featured.map(a => <ArticleCard key={a.slug} a={a} />)}
      </div>

      {CATEGORY_ORDER.map(c => {
        const inCat = articles.filter(a => a.category === c);
        if (inCat.length === 0) return null;
        return (
          <div key={c}>
            <div className="section-head">
              <h2>{cats[c].label}</h2>
              <Link to={`/category/${c}`}>All {cats[c].label.toLowerCase()} →</Link>
            </div>
            <div className="article-grid">
              {inCat.slice(0, 3).map(a => <ArticleCard key={a.slug} a={a} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
